/* eslint-disable react/no-unstable-nested-components */
/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */

import React, { RefObject } from 'react';
import classNames from 'classnames';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  PaginationState,
  useReactTable,
} from '@tanstack/react-table';
import { SingleValue } from 'react-select';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useConnectorErrorsQuery } from 'src/services/connector';
import { useProfile } from 'src/utils/ProfileService';
import ConnectorErrorModal, { ConnectorFeedError } from '../../modals/ConnectorErrorModal/ConnectorErrorModal';
import styles from '../ContactTable/ContactTable.module.scss';
import Select, { Option } from '../../ui/Select/Select';
import TablePagination from '../../ui/TablePagination/TablePagination';
import { formatDateAndTime, getPaginationOptions } from '../../../utils/general';

export interface ConnectorErrorsTableProps {
  containerRef: RefObject<HTMLDivElement>;
}

const columnHelper = createColumnHelper<ConnectorFeedError>();

const ConnectorErrorsTable: React.FC<ConnectorErrorsTableProps> = ({ containerRef }) => {
  const { t } = useTranslation();
  const { id } = useParams();
  const { profile } = useProfile();
  const [selectedError, setSelectedError] = React.useState<ConnectorFeedError | null>(null);

  const userLang = profile.lang ? profile.lang.replace('_', '-') : 'en-GB';
  const formatDate = (input: string) => formatDateAndTime(input, userLang);

  const getColumns = () => [
    columnHelper.accessor(row => row.created_at, {
      id: 'created_at',
      size: 200,
      meta: {
        label: t('general.date'),
      },
      header: ({ column }) => <span className="typo-text-12 text-faded">{column.columnDef.meta?.label}</span>,
      cell: info => <span className="typo-text-14 text-full whitespace-nowrap">{formatDate(info.getValue())}</span>,
    }),
    columnHelper.accessor(row => row.name, {
      id: 'name',
      size: 200,
      meta: {
        label: t('integrations.error_name'),
      },
      header: ({ column }) => <span className="typo-text-12 text-faded">{column.columnDef.meta?.label}</span>,
      cell: info => <span className="typo-text-14">{info.getValue()}</span>,
    }),
    columnHelper.accessor(row => row.message, {
      id: 'message',
      size: 400,
      meta: {
        label: t('integrations.error_message'),
      },
      header: ({ column }) => <span className="typo-text-12 text-faded">{column.columnDef.meta?.label}</span>,
      cell: info => (
        <span className="typo-text-14 block max-w-[520px] truncate" title={info.getValue() || undefined}>
          {info.getValue() || t('general.no_data')}
        </span>
      ),
    }),
  ];

  const rootRef = React.useRef<HTMLDivElement>(null);

  const options = getPaginationOptions();

  const [pagination, setPagination] = React.useState<PaginationState>({
    pageIndex: 0,
    pageSize: parseInt(options[0].value, 10),
  });

  const { data: connectorErrors } = useConnectorErrorsQuery(Number(id), {
    page: pagination.pageIndex + 1,
    size: pagination.pageSize,
  });

  const table = useReactTable({
    data: connectorErrors?.items || [],
    columns: getColumns(),
    state: {
      pagination,
    },
    enableSorting: false,
    columnResizeMode: 'onChange',
    enableColumnResizing: true,
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    manualPagination: true,
    getCoreRowModel: getCoreRowModel(),
  });

  const handlePerPageUpdate = (selectedOption: SingleValue<Option>) => {
    if (selectedOption) {
      setPagination({ pageIndex: 0, pageSize: Number(selectedOption.value) });
      if (containerRef) {
        containerRef.current?.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  const handleTablePaginationClick = (direction: 'previous' | 'next') => {
    const maxPage = connectorErrors?.pages ?? 0;
    if (direction === 'next' && (maxPage === 0 || pagination.pageIndex + 1 >= maxPage)) {
      return;
    }
    const newIndex = direction === 'next' ? pagination.pageIndex + 1 : pagination.pageIndex - 1;
    setPagination({ ...pagination, pageIndex: newIndex });
    if (containerRef) {
      containerRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className={(styles.tableContent, 'w-full relative')}>
      <div ref={rootRef} className={classNames(styles.root)}>
        <div className={styles.tableWrapper}>
          <table className="w-full">
            <thead>
              {table.getHeaderGroups().map(headerGroup => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map(header => (
                    <th
                      {...{
                        key: header.id,
                        colSpan: header.colSpan,
                        style: {
                          minWidth: header.getSize(),
                        },
                      }}>
                      <div className={styles.headerColumn}>
                        {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                      </div>
                      <div
                        {...{
                          onDoubleClick: () => header.column.resetSize(),
                          onMouseDown: header.getResizeHandler(),
                          onTouchStart: header.getResizeHandler(),
                          className: `resizer ${table.options.columnResizeDirection} ${
                            header.column.getIsResizing() ? 'isResizing' : ''
                          }`,
                          style: {
                            transform: '',
                          },
                        }}>
                        <div />
                      </div>
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.length > 0 ? (
                table.getRowModel().rows.map(row => (
                  <tr key={row.id}>
                    {row.getVisibleCells().map(cell => (
                      <td key={cell.id} className="cursor-pointer" onClick={() => setSelectedError(row.original)}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan={table.getAllColumns().length}
                    className="text-center w-full py-4 typo-text-14--bold text-faded">
                    <div className="flex justify-center items-center h-20">{t('general.no_data')}</div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="w-full my-20 flex items-center">
          <div className="flex gap-x-10 flex-1 items-center">
            <div>
              <Select onChange={handlePerPageUpdate} options={options} defaultValue={options[0]} />
            </div>
            <div className="typo-text-12 text-faded">{t('general.per_page')}</div>
          </div>
          <div className="ml-auto">
            <TablePagination
              maxPage={connectorErrors?.pages ?? 0}
              onPaginationClick={handleTablePaginationClick}
              currentPage={pagination.pageIndex}
            />
          </div>
        </div>
      </div>
      <div className={classNames(styles.rightShadow)} />
      <ConnectorErrorModal error={selectedError} onClose={() => setSelectedError(null)} formatDate={formatDate} />
    </div>
  );
};

export default ConnectorErrorsTable;
